/* eslint-disable @angular-eslint/prefer-standalone */
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ApiResponsePaged, IRequestSubmittedSummary } from '@eredes/dfcore';
import { DSM3ColumnTable, DSM3Pagination, DSM3SelectOption } from '@eredes/dsm3';
import { DFGUIListingViewModel } from '@eredes/microservices';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, last } from 'rxjs/operators';

import { GoogleAnalyticsEventsService } from 'app/_core/services/google-analytics-events.service';
import { LoadingService } from 'app/_core/services/loading.service';
import { UserService } from 'app/_core/services/user.service';
import { RequestFormHelper } from 'app/request-form/_helpers/request-form.helper';
import { SearchBarViewModel } from 'app/request-form/_models/search-bar.view-model';
import { RequestFormService } from 'app/request-form/_services/request-form.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-request-list-jump',
  templateUrl: './request-list-jump.component.html',
  styleUrls: ['./request-list-jump.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestListJumpComponent implements OnInit {
  columns: DSM3ColumnTable[] = [
    {
      label: 'ESTADO DE PEDIDO',
      prop: 'status_label',
    },
    {
      label: 'ID',
      prop: 'external_identification',
    },
    {
      label: 'CPE',
      prop: 'cpe',
    },
    {
      label: 'TIPO',
      prop: 'type_step_title',
    },
    {
      label: 'SUB-TIPO',
      prop: 'step_title',
    },
    {
      label: 'DATA DE CRIAÇÃO',
      prop: 'created_at',
    },
  ];

  readonly pagitionsOptions: DSM3SelectOption[] = [
    { label: '40', value: '40' },
    { label: '30', value: '30' },
    { label: '20', value: '20' },
    { label: '10', value: '10' },
  ];

  viewModel = new DFGUIListingViewModel();
  searchBarViewModel: SearchBarViewModel;
  envName = environment.envName;

  public readonly userService = inject(UserService);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly requestFormService = inject(RequestFormService);
  private readonly loadingService = inject(LoadingService);
  private readonly location = inject(Location);

  ngOnInit(): void {
    GoogleAnalyticsEventsService.emitEvent(
      'Pedidos (JUMP)',
      'Listagem de Pedidos de Operação e de Esclarecimento',
      'Visualiza listagem de Pedidos de Operação e de Esclarecimento'
    );

    const requestFilters = this.getStorageRequestFilter();
    const params = requestFilters
      ? requestFilters
      : {
          page: this.route.snapshot.queryParams.page ? this.route.snapshot.queryParams.page : 1,
          pageNum: this.route.snapshot.queryParams.pageNum ? this.route.snapshot.queryParams.pageNum : 10,
        };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
    });

    if (!this.userService.isProfileRetailer()) {
      this.addJumpStatus();
    }

    this.route.queryParams.pipe(debounceTime(250)).subscribe(() => {
      this.getRequests();
    });

    this.configureSearchBarViewModel();
    this.setupEventListeners();
  }

  /**
   * Method triggered when paging the table
   * @param {DSM3Pagination}  event pagination
   */
  onPagination(event: DSM3Pagination): void {
    const requestFilters = this.getStorageRequestFilter();

    const { page, itemsPerPage } = event;
    const params: Params = {};
    Object.assign(params, requestFilters);

    params.page = +params.pageNum !== itemsPerPage ? 1 : page;
    params.pageNum = itemsPerPage;

    this.setStorageRequestFilter(params);

    const url = this.router
      .createUrlTree([this.location.path().split('?')[0]], {
        queryParams: params,
      })
      .toString();
    this.router.navigateByUrl(url);
  }

  /**
   * Redirect to creating a new order
   */
  onCreateOrder(): void {
    this.router.navigate(['/request/create']);
  }

  /**
   * Method triggered when clicking on a table item
   * @param {IRequestSubmittedSummary} item table item
   */
  clickItem(item: IRequestSubmittedSummary): void {
    this.router.navigate([`/request/solicitation/detail/${item.uuid}`]);
  }

  /**
   * Find background color for status
   * @param {string} statusLabel - status label
   * @returns {string} - state background color
   */
  getStateBackgroundColor(statusLabel: string): string {
    return RequestFormHelper.getStateStyleByStatusName(statusLabel).bgState;
  }

  /**
   * Find text color for status
   * @param statusLabel
   * @returns
   */
  getStateTextColor(statusLabel: string): string {
    return RequestFormHelper.getStateStyleByStatusName(statusLabel).colorState;
  }

  /**
   * Export File csv by the currently active filter
   */
  onExport(): void {
    const { search, date, states, category, areas } = this.searchBarViewModel.formGroup.value;
    const filters = {
      page: this.route.snapshot.queryParams.page ? +this.route.snapshot.queryParams.page : 1,
      pageNum: this.route.snapshot.queryParams.pageNum ? +this.route.snapshot.queryParams.pageNum : 10,
      search,
      date,
      states,
      category,
      areas,
    };

    this.viewModel.requestOnGoing = true;
    this.loadingService.onLoadingChanged.next(true);

    this.requestFormService
      .exportRequests(filters)
      .pipe(debounceTime(250), last())
      .subscribe((result) => {
        const a = document.createElement('a');
        a.href = result.link;
        a.click();
        this.viewModel.requestOnGoing = false;

        GoogleAnalyticsEventsService.emitEvent(
          'Pedidos (JUMP)',
          'Exportação de Pedidos de Operação e de Esclarecimento',
          'Realiza exportação de Pedidos de Operação e de Esclarecimento'
        );

        this.loadingService.onLoadingChanged.next(false);

        this.cdr.detectChanges();
      });
  }

  /**
   * Retrieves the submitted forms/requests to list based on filters at url
   * @param changeFilter
   */
  getRequests(changeFilter = false): void {
    const { search, date, states, category, areas } = this.searchBarViewModel.formGroup.value;
    let filters = {
      page: this.route.snapshot.queryParams.page ? +this.route.snapshot.queryParams.page : 1,
      pageNum: this.route.snapshot.queryParams.pageNum ? +this.route.snapshot.queryParams.pageNum : 10,
      search,
      date,
      states,
      category,
      areas,
    };

    this.viewModel.requestOnGoing = true;

    const requestFilters = this.getStorageRequestFilter();
    if (changeFilter) {
      filters = {
        ...filters,
        page: 1,
        pageNum: requestFilters.pageNum ? requestFilters.pageNum : 10,
      };
    }

    if (!filters.date || (filters?.date?.length > 0 && !filters?.date[0])) {
      requestFilters.initialDate = null;
      requestFilters.finalDate = null;
    }
    requestFilters.search = filters.search;

    this.setStorageRequestFilter(requestFilters);

    this.requestFormService
      .getRequests(filters)
      .pipe(debounceTime(250), last())
      .subscribe((response: ApiResponsePaged<IRequestSubmittedSummary>) => {
        this.handleResponse(response);
      });
  }

  /**
   * Method to initialize listeners for executing some flow in the component
   */
  private setupEventListeners(): void {
    this.searchBarViewModel.formGroup.controls.states.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.onChangeFilters();
    });

    this.searchBarViewModel.formGroup.controls.category.valueChanges.subscribe((item) => {
      this.onChangeCategory(item);
      this.onChangeFilters();
    });

    this.searchBarViewModel.formGroup.controls.areas.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.onChangeFilters();
    });
  }

  /**
   * This function updates the URL query parameters based on the values of the search bar form inputs.
   */
  private onChangeFilters() {
    const { search, date, states, category, areas } = this.searchBarViewModel.formGroup.value;

    const params: Params = {};
    Object.assign(params, this.route.snapshot.queryParams);

    const requestFilters = this.getStorageRequestFilter();

    params.page = 1;
    params.pageNum = requestFilters.pageNum ? requestFilters.pageNum : 10;
    delete params.search;
    delete params.states;
    delete params.category;
    delete params.areas;

    if (search) {
      params.search = search;
    }

    if (date && date.length > 1 && date[0]) {
      params.initialDate = moment(date[0]).format('YYYY-MM-DD');
      params.finalDate = moment(date[1]).format('YYYY-MM-DD');
    } else {
      params.initialDate = null;
      params.finalDate = null;
    }

    if (states && states.length > 0) {
      params.states = states.join();
    }

    if (category) {
      params.category = category;
    }

    if (areas && areas.length > 0) {
      params.areas = areas.join();
    }

    this.setStorageRequestFilter(params);

    const url = this.router
      .createUrlTree([this.location.path().split('?')[0]], {
        queryParams: params,
      })
      .toString();

    this.router.navigateByUrl(url);
  }

  /**
   * Method called when the category field is changed
   * @param value new value of category
   */
  private onChangeCategory(value): void {
    if (value) {
      const areas = this.requestFormService.getAreasByCategory(value);
      this.searchBarViewModel.$areas.next(areas);
    } else {
      this.searchBarViewModel.$areas.next([]);
    }
    this.searchBarViewModel.formGroup.controls.areas.reset();
  }

  /**
   * Configure the information necessary for the search filter bar to work
   */
  private configureSearchBarViewModel(): void {
    const requestFilters = this.getStorageRequestFilter();

    let params;
    if (requestFilters) {
      params = requestFilters;
    } else {
      params = {
        ...this.route.snapshot.queryParams,
      };
    }

    let { search, states, category, areas } = params;
    const { initialDate, finalDate } = params;

    search = search || '';
    states = states ? states.split(',') : [];
    category = category || '';
    areas = areas ? areas.split(',') : [];

    let newInitialDate = null;
    let newFinalDate = null;
    if (initialDate && finalDate) {
      newInitialDate = moment(initialDate, 'YYYY-MM-DD').toDate();
      newFinalDate = moment(finalDate, 'YYYY-MM-DD').toDate();
    } else if (!requestFilters) {
      newInitialDate = moment(new Date()).subtract(30, 'days').toDate();
      newFinalDate = moment(new Date()).toDate();
    }

    params = {
      ...params,
      initialDate: newInitialDate ? moment(newInitialDate).format('YYYY-MM-DD') : null,
      finalDate: newFinalDate ? moment(newFinalDate).format('YYYY-MM-DD') : null,
    };
    this.setStorageRequestFilter(params);

    const date = newInitialDate && newFinalDate ? [newInitialDate, newFinalDate] : [null, null];

    this.searchBarViewModel = new SearchBarViewModel(
      this.fb.group({
        search: [search ? search : '', [], []],
        date: this.fb.array(date, [], []),
        states: this.fb.array(states, [], []),
        category: [category],
        areas: this.fb.array(areas, [], []),
      }),
      new BehaviorSubject<DSM3SelectOption[]>([
        { value: 'in_progress', label: 'Em curso E-REDES' },
        { value: 'pending_retailer', label: 'Pendente Comercializador' },
        { value: 'solved', label: 'Resolvido' },
        { value: 'canceled', label: 'Anulado' },
      ]),
      new BehaviorSubject<DSM3SelectOption[]>(this.requestFormService.getCategories()),
      new BehaviorSubject<DSM3SelectOption[]>([])
    );

    if (category) {
      areas = this.requestFormService.getAreasByCategory(category);
      this.searchBarViewModel.$areas.next(areas);
    }
  }

  /**
   * Handle request listing service response
   * @param response response of request listing service response
   */
  private handleResponse(response: ApiResponsePaged<IRequestSubmittedSummary>): void {
    this.viewModel.requests = [];
    if (response && response.data) {
      this.viewModel.requests = response.data;
      this.viewModel.can_create = response.can_create;

      this.viewModel.paginationData.total = +response.total;
      this.viewModel.paginationData.from = +response.from;
      this.viewModel.paginationData.to = +response.to;
      this.viewModel.paginationData.currentPage = +response.current_page;
      this.viewModel.paginationData.lastPage = +response.last_page;
      this.viewModel.paginationData.perPage = +response.per_page;

      // hide loading
      this.viewModel.requestOnGoing = false;
    }

    this.cdr.detectChanges();
  }

  /**
   * Add JUMP state column
   */
  private addJumpStatus(): void {
    const jumpStatus = {
      label: 'ESTADO JUMP',
      prop: 'external_status_description',
    };
    this.columns.splice(1, 0, jumpStatus);
  }

  /**
   * This function retrieves a request filter object from session storage or returns an empty object if
   * none exists.
   * @returns The `getStorageRequestFilter()` function returns an object that represents the request
   * filters stored in the session storage. If there are no request filters stored, it returns an empty
   * object.
   */
  private getStorageRequestFilter() {
    const requestFilters = sessionStorage.getItem('request_filters');

    return requestFilters ? JSON.parse(requestFilters) : null;
  }

  /**
   * This function sets the request filters in the session storage if they exist.
   * @param requestFilters - requestFilters is a parameter that represents an object containing filters
   * to be applied to a request. It is used in the function to set the filters in the session storage.
   * If the parameter is not null or undefined, it is converted to a JSON string and stored in the
   * session storage under the key 'request
   */
  private setStorageRequestFilter(requestFilters) {
    if (requestFilters) {
      sessionStorage.setItem('request_filters', JSON.stringify(requestFilters));
    }
  }
}
